import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, ShieldCheck, FlaskConical } from "lucide-react";

const ELEMENTS = ["Au", "Ag", "Cu", "Pt", "Pd", "Ni", "Zn", "Pb", "Cd"];

const presets = [
  { name: "22 Ayar (Beklenen)", data: { Au: 91.6, Ag: 5.0, Cu: 3.4 } },
  { name: "Sahte Şüpheli",       data: { Au: 78.0, Ag: 4.0, Cu: 12.0, Ni: 3.5, Zn: 2.5 } },
  { name: "925 Gümüş",           data: { Ag: 92.5, Cu: 7.5 } },
];

export default function Spectrometer() {
  const [sample, setSample] = useState("");
  const [els, setEls] = useState({ Au: 91.6, Ag: 5.0, Cu: 3.4 });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const setEl = (k, v) => setEls((e) => ({ ...e, [k]: Number(v) }));

  const applyPreset = (p) => setEls(p.data);

  const analyze = async () => {
    setLoading(true);
    try {
      const r = await api.post("/spectrometer/analyze", { sample_name: sample, elements: els });
      setResult(r.data);
      if (r.data.alarm) toast.error("KIRMIZI ALARM: Şüpheli örnek");
      else toast.success("Örnek referansa uygun");
    } catch (e) {
      toast.error("Hata");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="spectro-page">
      <div>
        <div className="label-caps">Anti-Counterfeit Shield</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 font-light">Sahtecilik Kalkanı</h1>
        <p className="text-neutral-400 mt-3 max-w-2xl">
          Spektrometre cihazından gelen element oranlarını referans standartlar ile karşılaştırın.
          Şüpheli metaller (Ni, Zn, Pb, Cd) yüksekse anında kırmızı alarm.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-flat p-8" data-testid="spectro-input">
          <div className="label-caps mb-2">Girdi</div>
          <div className="font-display text-2xl mb-6 flex items-center gap-2"><FlaskConical size={22} /> Element Oranları (%)</div>

          <input
            data-testid="sample-name"
            placeholder="Numune adı (ops.)"
            value={sample} onChange={(e)=>setSample(e.target.value)}
            className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-1 focus:ring-[--gold]"
          />

          <div className="flex flex-wrap gap-2 mb-4">
            {presets.map((p) => (
              <button key={p.name} onClick={()=>applyPreset(p)} className="btn-gold" data-testid={`preset-${p.name}`}>
                {p.name}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-3">
            {ELEMENTS.map((e) => (
              <div key={e}>
                <label className="label-caps block mb-1">{e}</label>
                <input
                  data-testid={`el-${e}`}
                  type="number" step="0.01"
                  value={els[e] ?? ""}
                  onChange={(ev)=>setEl(e, ev.target.value || 0)}
                  className="w-full bg-transparent border border-white/10 px-2 py-2 text-sm font-mono tabular-nums focus:outline-none focus:ring-1 focus:ring-[--gold]"
                />
              </div>
            ))}
          </div>

          <button onClick={analyze} disabled={loading} className="btn-primary-gold w-full mt-6" data-testid="spectro-analyze">
            {loading ? "Analiz Ediliyor…" : "Analiz Et"}
          </button>
        </div>

        <div className={`card-flat p-8 ${result?.alarm ? "border-red-500/60" : ""}`} data-testid="spectro-result" style={result?.alarm ? { borderColor: "rgba(239,68,68,0.6)" } : {}}>
          <div className="label-caps mb-4">Sonuç</div>
          {!result && <div className="text-neutral-500 text-sm">Örnek analiz edildiğinde sonuçlar burada gösterilecek.</div>}
          {result && (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                {result.alarm ? (
                  <><AlertTriangle className="text-red-500" size={32} />
                    <div>
                      <div className="font-display text-3xl text-red-400" data-testid="spectro-verdict">KIRMIZI ALARM</div>
                      <div className="label-caps">Sahte / Şüpheli</div>
                    </div>
                  </>
                ) : (
                  <><ShieldCheck size={32} style={{color:"var(--gold)"}} />
                    <div>
                      <div className="font-display text-3xl" style={{color:"var(--gold)"}} data-testid="spectro-verdict">GÜVENLİ</div>
                      <div className="label-caps">Referansa Uygun</div>
                    </div>
                  </>
                )}
              </div>

              {result.best_match && (
                <div className="border border-white/5 p-4">
                  <div className="label-caps mb-1">En İyi Eşleşme</div>
                  <div className="font-mono">{result.best_match.reference}</div>
                  <div className="text-xs text-neutral-400 mt-1">Sapma: <span className="font-mono">{result.best_match.deviation}</span></div>
                </div>
              )}

              {result.suspicious_elements?.length > 0 && (
                <div className="border border-red-500/40 p-4">
                  <div className="label-caps text-red-400">Şüpheli Elementler</div>
                  <ul className="mt-2 space-y-1 text-sm">
                    {result.suspicious_elements.map((s, i) => (
                      <li key={i} className="flex justify-between">
                        <span>{s.element}</span>
                        <span className="font-mono tabular-nums">{s.percent}%</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="border-t border-white/5 pt-4">
                <div className="label-caps mb-2">Toplam</div>
                <div className="font-mono tabular-nums text-2xl" style={{color:"var(--gold)"}}>{result.total_pct}%</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
